import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  X2PropRecOnpInitModel,
  X2PropRecOnpInitVariables,
  X2PropRecOnpModel,
  X2PropRecOnpVariables,
} from '../../../../models/email-template/x2-prop-rec-onp.interface';

/**
 * Mockup-styled 2x Property Recommendation Old/New Price block - a drop-in
 * replacement for app-x2-prop-rec-onp in block-renderer-v2 (same
 * [block]/[isPreview] inputs and outputs), so it covers both the
 * center-canvas summary (isPreview=true) and the right-panel edit
 * form (isPreview=false). Reuses the real, unmodified app-block-text (image
 * upload/segment/validation), app-field-editor-v2 and app-pin sub-components
 * and the exact same X2PropRecOnpVariables field names - only the
 * surrounding layout is new. Like x2-prop-rec-v2 but each card shows an
 * old/new price pair instead of a destination name.
 */
@Component({
  selector: 'app-x2-prop-rec-onp-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './x2-prop-rec-onp-v2.component.html',
  styleUrl: './x2-prop-rec-onp-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class X2PropRecOnpComponentV2 extends BaseComponent implements OnInit {
  @Input() block: X2PropRecOnpModel = new X2PropRecOnpInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new X2PropRecOnpInitVariables();
    }
  }

  image1Placeholder = 'First Image';
  image2Placeholder = 'Second Image';
  image1HrefPlaceholder = 'Enter First Image Redirect Link';
  image2HrefPlaceholder = 'Enter Second Image Redirect Link';
  image1AltPlaceholder = 'Enter First Image Alt Text';
  image2AltPlaceholder = 'Enter Second Image Alt Text';
  name1Placeholder = 'Enter First Name';
  name2Placeholder = 'Enter Second Name';
  oldPrice1Placeholder = 'Enter First Old Price';
  newPrice1Placeholder = 'Enter First New Price';
  oldPrice2Placeholder = 'Enter Second Old Price';
  newPrice2Placeholder = 'Enter Second New Price';
  ctaLink1Placeholder = 'Enter First CTA Link';
  ctaLink2Placeholder = 'Enter Second CTA Link';
  ctaText1Placeholder = 'Enter First CTA Text';
  ctaText2Placeholder = 'Enter Second CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected image1AsUpload = true;
  protected image2AsUpload = true;

  protected toggleImage1Mode(): void {
    this.image1AsUpload = !this.image1AsUpload;
  }

  protected toggleImage2Mode(): void {
    this.image2AsUpload = !this.image2AsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get card1ImageHref(): string {
    return this.hrefFor(this.block.variables?.['X2Onp1ImageHref'], this.block.variables?.['X2Onp1ImageHrefSegmentRegex']);
  }

  get card2ImageHref(): string {
    return this.hrefFor(this.block.variables?.['X2Onp2ImageHref'], this.block.variables?.['X2Onp2ImageHrefSegmentRegex']);
  }

  get card1CtaHref(): string {
    return this.hrefFor(this.block.variables?.['X2Onp1CtaLink'], this.block.variables?.['X2Onp1CtaLinkSegmentRegex']);
  }

  get card2CtaHref(): string {
    return this.hrefFor(this.block.variables?.['X2Onp2CtaLink'], this.block.variables?.['X2Onp2CtaLinkSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof X2PropRecOnpVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
